import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Stack,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  QrCode2 as QrCodeIcon,
  CloudDownload as DownloadIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createBulkQRCodes, downloadQRImage, generateQRImageUrl } from '../qrCodeApi';

const GenerateQRCodes = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [count, setCount] = useState(10);
  const [baseUrl] = useState('https://play.google.com/store/apps/details?id=com.w3h333.storesye');
  const [generatedQRCodes, setGeneratedQRCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const steps = [t('setQuantity') || 'Set Quantity', t('generate') || 'Generate', t('downloadQRs') || 'Download QRs'];

  const handleGenerate = async () => {
    if (count < 1 || count > 100) {
      Swal.fire(t('error'), t('qrCountError') || 'Please enter a number between 1 and 100', 'error');
      return;
    }

    setLoading(true);
    try {
      const result = await createBulkQRCodes(count, baseUrl);
      setGeneratedQRCodes(result.qrCodes);
      setActiveStep(2);
      Swal.fire({
        icon: 'success',
        title: t('success'),
        text: `${result.qrCodes.length} ${t('qrCodesGenerated') || 'QR codes generated successfully'}`,
        timer: 2000
      });
    } catch (error) {
      Swal.fire(t('error'), t('generateError') || 'Failed to generate QR codes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    for (const qr of generatedQRCodes) {
      try {
        await downloadQRImage(qr);
        await new Promise(resolve => setTimeout(resolve, 500)); // Delay between downloads
      } catch (error) {
        console.error(`Error downloading QR ${qr.Code}:`, error);
      }
    }
    Swal.fire({
      icon: 'success',
      title: t('success'),
      text: t('allQRsDownloaded') || 'All QR codes downloaded successfully'
    });
  };

  const handleDownloadSingle = async (qr) => {
    try {
      await downloadQRImage(qr);
    } catch (error) {
      Swal.fire(t('error'), t('downloadError') || 'Download failed', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <QrCodeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                {t('generateQRCodes') || 'Generate QR Codes'}
              </Typography>
            </Stack>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin/qrcodes')}
              sx={{ borderRadius: 50 }}
            >
              {t('back')}
            </Button>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('qrGenerateInfo') || 'Generate multiple QR codes at once. Each QR code will have a unique tracking code.'}
              </Alert>
              
              <TextField
                type="number"
                label={t('numberOfQRCodes') || 'Number of QR Codes'}
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value) || 0)}
                fullWidth
                inputProps={{ min: 1, max: 100 }}
                helperText={t('qrCountHelper') || 'Enter a number between 1 and 100'}
                sx={{ mb: 3 }}
              />

              <TextField
                label={t('appURL') || 'App URL'}
                value={baseUrl}
                fullWidth
                disabled
                helperText={t('defaultURLInfo') || 'Default Google Play Store URL'}
                sx={{ mb: 3 }}
              />

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => setActiveStep(1)}
                disabled={count < 1 || count > 100}
                sx={{ borderRadius: 50, px: 5 }}
              >
                {t('continue')}
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {t('confirmGeneration') || 'Confirm QR Code Generation'}
              </Typography>
              <Paper sx={{ p: 3, my: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body1" gutterBottom>
                  {t('youAreAboutToGenerate') || 'You are about to generate:'}
                </Typography>
                <Typography variant="h3" color="primary.main" fontWeight="bold" sx={{ my: 2 }}>
                  {count}
                </Typography>
                <Typography variant="body1">
                  {t('qrCodes')}
                </Typography>
              </Paper>

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(0)}
                  sx={{ borderRadius: 50 }}
                >
                  {t('back')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerate}
                  loading={loading}
                  disabled={loading}
                  sx={{ borderRadius: 50, px: 5 }}
                >
                  {loading ? t('generating') || 'Generating...' : t('generate')}
                </Button>
              </Stack>
            </Box>
          )}

          {activeStep === 2 && generatedQRCodes.length > 0 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6">
                  {t('generatedQRCodes') || 'Generated QR Codes'} ({generatedQRCodes.length})
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadAll}
                  sx={{ borderRadius: 50 }}
                >
                  {t('downloadAll') || 'Download All'}
                </Button>
              </Stack>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                {generatedQRCodes.map((qr) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={qr.Id}>
                    <Paper sx={{ p: 2, textAlign: 'center', '&:hover': { boxShadow: 6 } }}>
                      <img
                        src={generateQRImageUrl(qr.TrackingUrl)}
                        alt={`QR-${qr.Code}`}
                        style={{ width: '100%', height: 'auto', maxWidth: 200 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, fontFamily: 'monospace', fontWeight: 'bold' }}
                      >
                        {qr.Code}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadSingle(qr)}
                        sx={{ mt: 1, borderRadius: 50 }}
                      >
                        {t('download')}
                      </Button>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/admin/qrcodes')}
                  sx={{ borderRadius: 50 }}
                >
                  {t('backToQRCodes') || 'Back to QR Codes'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default GenerateQRCodes;
